/* eslint-disable @typescript-eslint/no-explicit-any */
export { formatNumberWithSeparator } from '@hyphengroup/cms-components/utils/helpers/formatters';
import { tr as baseTr } from '@hyphengroup/cms-components/utils/translations/tr';
import { CONTENT_MHG } from './translations/mhg';
import { capitalizeFirstLetter } from '$/shared/capitalize';
export { getUrgencyPhase } from '@hyphengroup/cms-components/utils/helpers/calculateRewardValue';
export { mapOfferToPeriodGifts } from '@hyphengroup/cms-components/utils/helpers/offers';
export { navbarLinkToUrl } from '@hyphengroup/cms-components/cms-blocks/navigation/utils/utils';

export const slugify = function (str: string): string {
  str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens
  return str;
};

/**
 * Effective "content update" date for an article/page. Prefers the
 * author-controlled `dateModified` (advances only on genuine content edits),
 * falling back to Strapi's `updatedAt` then `publishedAt` for entities that
 * predate the field. Single source of truth so the visible "Last updated" line
 * and the BlogPosting `dateModified` schema always hold the same value.
 */
export const getContentUpdatedDate = (page: {
  dateModified?: string;
  updatedAt?: string;
  publishedAt?: string;
}): string | undefined =>
  page.dateModified ?? page.updatedAt ?? page.publishedAt;

/** `YYYY-MM-DD` with no time part, which is how Strapi serialises a `date`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const formatDatePageTitle = (
  dateString: string,
  locale: string = 'en-US',
): string => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    // A date-only string parses as UTC midnight, so rendering it in the viewer's
    // zone moves it to the previous day anywhere west of Greenwich — and, in a
    // client component under a statically rendered page, disagrees with what the
    // server already put in the HTML. The written day is the whole value here;
    // there is no instant to convert.
    ...(DATE_ONLY.test(dateString) ? { timeZone: 'UTC' } : {}),
  };

  return date.toLocaleDateString(locale, options);
};

export const removeTags = (htmlString: string): string => {
  if (typeof htmlString !== 'string') return '';
  return htmlString.replace(/<\/?[^>]+(>|$)/g, '');
};

export const truncateText = (text: string, maxLength = 60): string => {
  return text && text.length > maxLength
    ? `${text.substring(0, maxLength)}...`
    : text;
};

/**
 * Truncate `text` to a character `budget` for a collapsed teaser view (CMS-1900).
 *
 * Operates on Unicode code points (`[...text]`), not UTF-16 code units, so the
 * budget counts real characters and a cut never splits a surrogate pair — emoji or
 * non-BMP glyphs (HKSCS / CJK Extension B such as 𡃁, standard in written Cantonese)
 * are kept whole instead of leaving a lone surrogate that renders as `�`.
 *
 * - `wordBoundary: true` (space-delimited languages, e.g. en-*) backs the cut up to
 *   the previous whitespace so it never splits a word. Whitespace is matched by
 *   `\s`, which covers NBSP (U+00A0) and other Unicode spaces common in Strapi copy
 *   pasted from Word / Google Docs.
 * - `wordBoundary: false` (zh-*) cuts on a whole-character boundary, but still backs
 *   up when the cut would land inside an embedded ASCII/number token (bank names,
 *   APR figures like "3.88"), so those tokens are never split mid-way.
 * - Trailing separator/terminator punctuation and whitespace are stripped before a
 *   single ellipsis (…) is appended. The strip set is limited to sentence
 *   punctuation, so it never eats a unit such as "%" or a closing bracket.
 *
 * Returns the original text with `isTruncated: false` when it already fits the
 * budget, so callers can decide whether to render a "show more" toggle.
 */
export const truncateToBudget = (
  text: string,
  budget: number,
  { wordBoundary }: { wordBoundary: boolean },
): { text: string; isTruncated: boolean } => {
  const chars = [...text];
  if (budget <= 0 || chars.length <= budget) {
    return { text, isTruncated: false };
  }

  const isSpace = (ch: string | undefined) =>
    ch !== undefined && /\s/u.test(ch);
  const isAlnum = (ch: string | undefined) =>
    ch !== undefined && /[0-9A-Za-z]/.test(ch);
  // Digits, letters and the inner separators of a number/identifier ("3.88").
  const isTokenChar = (ch: string | undefined) =>
    ch !== undefined && /[0-9A-Za-z.]/.test(ch);

  let end = budget;

  if (wordBoundary) {
    // en-*: retreat to the previous whitespace when the cut lands inside a word.
    if (!isSpace(chars[end]) && !isSpace(chars[end - 1])) {
      let w = end;
      while (w > 0 && !isSpace(chars[w - 1])) w--;
      // Keep the hard cut for a single over-long word (no earlier whitespace).
      if (w > 0) end = w;
    }
  } else if (isAlnum(chars[end - 1]) && isAlnum(chars[end])) {
    // zh-*: character-boundary cut, but don't split an embedded ASCII/number token.
    let a = end;
    while (a > 0 && isTokenChar(chars[a - 1])) a--;
    if (a > 0) end = a;
  }

  const clipped = chars.slice(0, end).join('');
  // Sentence punctuation only (ASCII + common full-width) — never "%" or brackets.
  const trimmed = clipped.replace(/[\s,.;:!?，。、；：！？]+$/u, '');

  return { text: `${trimmed || clipped}…`, isTruncated: true };
};

export const generateButtonSubText = (props: {
  btnLabel?: string;
  providerName: string;
  appName: string;
  lang: string;
}): string => {
  const validLabels = [
    tr('express_buy', props.lang),
    tr('read_more', props.lang),
    tr('learn_more', props.lang),
  ]; // Add all labels to compare
  if (props.btnLabel && props.btnLabel === tr('express_buy', props.lang)) {
    return tr('on_provider_website', props.lang, {
      provider: capitalizeFirstLetter(props.appName),
    });
  }

  return props.btnLabel && validLabels.includes(props.btnLabel)
    ? tr('on_app', props.lang, { app: capitalizeFirstLetter(props.appName) })
    : tr('on_provider_website', props.lang, { provider: props.providerName });
};

export type TranslationKey = keyof ReturnType<(typeof CONTENT_MHG)['get']>;

export function tr(
  key: TranslationKey,
  lang: string,
  replacements?: Record<string, string>,
  count?: number,
): string {
  const staticText = CONTENT_MHG.get(lang);
  const res = baseTr(key as any, staticText, replacements, count);
  return res;
}
